import { initializeApp } from 'firebase/app'
import {
  forceWebSockets,
  getDatabase,
  goOffline,
  goOnline,
  onChildChanged as onChildChangedRaw,
  onValue,
  ref,
} from 'firebase/database'

forceWebSockets()

const app = initializeApp({
  apiKey: 'AIzaSyCT3EHuCEdlEz3F_PGhhqzxp_wTX_4mFWA',
  authDomain: 'loving-loyalty-1590a.firebaseapp.com',
  projectId: 'loving-loyalty-1590a',
  storageBucket: 'loving-loyalty-1590a.appspot.com',
  messagingSenderId: '253433069603',
  appId: '1:253433069603:web:0cfdea55289d748a557196',
  measurementId: 'G-WM2SHM0LCT',
  databaseURL:
    'https://loving-loyalty-1590a-default-rtdb.europe-west1.firebasedatabase.app',
})

export const firebasePrefix = import.meta.env.VITE_FIREBASE_PREFIX ?? 'test'

export function getOnce<T>(path: string): Promise<T | null> {
  return new Promise((resolve) => {
    // The RTDB SDK (and this module's tests) may invoke the onValue/onError
    // callback synchronously, before the call below has finished assigning
    // its return value to `state.unsubscribe`. Track that case with a flag
    // instead of reading `state.unsubscribe` from inside the callback, and
    // unsubscribe once assignment has actually happened. (A plain `let`
    // reassigned once trips `prefer-const`, but `const` would read
    // `unsubscribe` from the TDZ during that synchronous callback — so the
    // mutable slot lives on an object property instead.)
    const state: { unsubscribe?: () => void; firedBeforeAssigned: boolean } = {
      firedBeforeAssigned: false,
    }

    const finish = (value: T | null) => {
      if (state.unsubscribe) {
        state.unsubscribe()
      } else {
        state.firedBeforeAssigned = true
      }
      resolve(value)
    }

    state.unsubscribe = onValue(
      ref(getDatabase(app), path),
      (snapshot) => finish((snapshot.val() as T | null) ?? null),
      (error) => {
        console.error('[Firebase] read failed', path, error)
        finish(null)
      },
    )

    if (state.firedBeforeAssigned) {
      state.unsubscribe()
    }
  })
}

export function onChildChanged(path: string, callback: (value: unknown) => void): () => void {
  return onChildChangedRaw(ref(getDatabase(app), path), (snapshot) => {
    callback(snapshot.val())
  })
}

export function monitorConnection(
  onDisconnect: () => void,
  onReconnect: () => void,
): () => void {
  return onValue(ref(getDatabase(app), '.info/connected'), (snapshot) => {
    if (snapshot.val() === false) {
      console.warn('[Firebase] Disconnected from Realtime Database')
      onDisconnect()
    } else {
      console.info('[Firebase] Connected to Realtime Database')
      onReconnect()
    }
  })
}

/** `reconnect()` bounces the socket by going offline and back on, which makes
 *  `.info/connected` report a disconnect that we caused ourselves. Without
 *  this flag the connection monitor treats that as a real drop and schedules
 *  another reconnect, which bounces the socket again — a loop that never
 *  settles. The window is cleared on a timer rather than on the next connect
 *  so that a reconnect which never succeeds still re-arms. */
let selfReconnecting = false
let selfReconnectTimer: ReturnType<typeof setTimeout> | null = null

export function isSelfReconnecting(): boolean {
  return selfReconnecting
}

export function reconnect(): void {
  const db = getDatabase(app)

  selfReconnecting = true
  if (selfReconnectTimer) clearTimeout(selfReconnectTimer)
  selfReconnectTimer = setTimeout(() => {
    selfReconnecting = false
    selfReconnectTimer = null
  }, 6000)

  goOffline(db)
  setTimeout(() => goOnline(db), 2000)
}
